import type { ChartSeries, ChartValueFormat } from '@fruitful/types';
import { View as RNView } from 'react-native';
import Svg, { Circle, Path } from 'react-native-svg';
import { BodySmallRegular, H1Regular, H2Regular, YStack } from 'smoothie';

import { formatValue } from './chartUtils';
import { buildDonutSlicePath, SLICE_GAP } from './donutGeometry';

const MAX_DONUT_RADIUS = 146;
const INNER_RADIUS_RATIO = 0.74;
const DIMMED_SLICE_OPACITY = 0.5;
const SELECTED_OUTLINE_WIDTH = 1;
const FULL_CIRCLE = Math.PI * 2;

export interface SliceColors {
    fill: string;
    /** Outline drawn around the slice while it is selected. */
    outline: string;
}

/** react-native-svg's web typings expect press handlers to return a value. */
const svgPressHandler = (onPress: () => void) => () => {
    onPress();
    return {};
};

/** A lone slice covers the whole circle, so it has no gutters or corners. */
const FullRing = ({
    colors,
    center,
    outerRadius,
    innerRadius,
    isSelected,
    isDimmed,
    onPress,
}: {
    colors: SliceColors;
    center: number;
    outerRadius: number;
    innerRadius: number;
    isSelected: boolean;
    isDimmed: boolean;
    onPress: () => void;
}) => {
    const halfOutline = SELECTED_OUTLINE_WIDTH / 2;

    return (
        <>
            <Circle
                cx={center}
                cy={center}
                r={(outerRadius + innerRadius) / 2}
                fill="none"
                stroke={colors.fill}
                strokeWidth={outerRadius - innerRadius}
                opacity={isDimmed ? DIMMED_SLICE_OPACITY : 1}
                onPress={svgPressHandler(onPress)}
            />
            {isSelected
                ? [outerRadius - halfOutline, innerRadius + halfOutline].map(
                      (edgeRadius) => (
                          <Circle
                              key={edgeRadius.toString()}
                              cx={center}
                              cy={center}
                              r={edgeRadius}
                              fill="none"
                              stroke={colors.outline}
                              strokeWidth={SELECTED_OUTLINE_WIDTH}
                              pointerEvents="none"
                          />
                      ),
                  )
                : null}
        </>
    );
};

export const DonutChart = ({
    series,
    valueFormat,
    chartWidth,
    resolveSliceColors,
    selectedIndex,
    onSelectIndex,
}: {
    series: ChartSeries;
    valueFormat: ChartValueFormat;
    chartWidth: number;
    resolveSliceColors: (
        pointColor: string | undefined,
        seriesColor: string | undefined,
        index: number,
    ) => SliceColors;
    selectedIndex: number | null;
    onSelectIndex: (index: number) => void;
}) => {
    const radius = Math.min(MAX_DONUT_RADIUS, (chartWidth - 16) / 2);
    const innerRadius = radius * INNER_RADIUS_RATIO;
    const midRadius = (radius + innerRadius) / 2;
    const diameter = radius * 2;
    const values = series.points.map((point) => Math.max(point.value, 0));
    const total = values.reduce((sum, value) => sum + value, 0);
    const selectedPoint =
        selectedIndex != null ? series.points[selectedIndex] : undefined;
    const gapAngle = SLICE_GAP / midRadius;

    const boundaries = values.reduce<number[]>(
        (angles, value) => [
            ...angles,
            (angles[angles.length - 1] ?? 0) +
                (total === 0 ? 0 : (value / total) * FULL_CIRCLE),
        ],
        [0],
    );
    const onlyVisibleIndex =
        values.filter((value) => value > 0).length === 1
            ? values.findIndex((value) => value > 0)
            : -1;

    return (
        <RNView
            accessibilityLabel="Donut chart"
            style={{ width: diameter, height: diameter }}
        >
            <Svg
                width={diameter}
                height={diameter}
                viewBox={`0 0 ${diameter.toString()} ${diameter.toString()}`}
            >
                {onlyVisibleIndex >= 0 ? (
                    <FullRing
                        colors={resolveSliceColors(
                            series.points[onlyVisibleIndex]?.color,
                            series.color,
                            onlyVisibleIndex,
                        )}
                        center={radius}
                        outerRadius={radius}
                        innerRadius={innerRadius}
                        isSelected={selectedIndex === onlyVisibleIndex}
                        isDimmed={
                            selectedIndex != null &&
                            selectedIndex !== onlyVisibleIndex
                        }
                        onPress={() => {
                            onSelectIndex(onlyVisibleIndex);
                        }}
                    />
                ) : (
                    series.points.map((point, index) => {
                        const startAngle =
                            (boundaries[index] ?? 0) + gapAngle / 2;
                        const endAngle =
                            (boundaries[index + 1] ?? 0) - gapAngle / 2;

                        if (endAngle <= startAngle) {
                            return null;
                        }

                        const isSelected = selectedIndex === index;
                        const colors = resolveSliceColors(
                            point.color,
                            series.color,
                            index,
                        );

                        return (
                            <Path
                                key={`${point.label}-${index.toString()}`}
                                d={buildDonutSlicePath({
                                    center: radius,
                                    outerRadius: radius,
                                    innerRadius,
                                    startAngle,
                                    endAngle,
                                })}
                                fill={colors.fill}
                                {...(isSelected
                                    ? {
                                          stroke: colors.outline,
                                          strokeWidth: SELECTED_OUTLINE_WIDTH,
                                      }
                                    : {})}
                                opacity={
                                    selectedIndex == null || isSelected
                                        ? 1
                                        : DIMMED_SLICE_OPACITY
                                }
                                onPress={svgPressHandler(() => {
                                    onSelectIndex(index);
                                })}
                            />
                        );
                    })
                )}
            </Svg>
            <RNView
                pointerEvents="none"
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <YStack alignItems="center" justifyContent="center">
                    {selectedPoint == null ? (
                        <H2Regular color="$black">
                            {formatValue(total, valueFormat)}
                        </H2Regular>
                    ) : (
                        <>
                            <H1Regular color="$black">
                                {`${Math.round(
                                    total === 0
                                        ? 0
                                        : (Math.max(selectedPoint.value, 0) /
                                              total) *
                                              100,
                                ).toString()}%`}
                            </H1Regular>
                            <BodySmallRegular color="$gray1000">
                                {formatValue(selectedPoint.value, valueFormat)}
                            </BodySmallRegular>
                        </>
                    )}
                </YStack>
            </RNView>
        </RNView>
    );
};
